import { useCallback } from 'react';

import { useAppStore, getAppStore } from '@/store';
import pollUntilCompleted from '@/requests/magic-flow/pollUntilCompleted';
import { clearGeneration } from '@/utils/storage/generationStorage';
import { MAGIC_FLOW_WEBHOOK_URL } from '@/consts/urls';
import buildFlowMedia from '@/utils/generation/buildFlowMedia';
import renameDriveFile from '@/utils/generation/renameDriveFile';
import decidePostGenerationUX from '@/utils/generation/decidePostGenerationUX';

import type { IAppStore } from '@/store';
import type { FlowMedia } from '@/types';

export interface PollingResult {
  resultFile: FlowMedia;
  isVideo: boolean;
  isAudio: boolean;
  driveUid?: string;
  width?: number;
  height?: number;
}

const selector = (store: IAppStore) => ({
  setResultFile: store.setResultFile,
  setGenerationTimestamp: store.setGenerationTimestamp,
  setLocalNotification: store.setLocalNotification,
  updateContentFile: store.updateContentFile,
  setPendingLightboxFileId: store.setPendingLightboxFileId,
  setIsResultMode: store.setIsResultMode,
});

/**
 * Hook for handling generation polling and result processing
 */
const useGenerationPolling = () => {
  const {
    setResultFile,
    setGenerationTimestamp,
    setLocalNotification,
    updateContentFile,
    setPendingLightboxFileId,
    setIsResultMode,
  } = useAppStore(selector);

  const poll = useCallback(
    async (
      generationId: string,
      placeholderUuid: string,
      presetTitle: string
    ): Promise<PollingResult> => {
      const pollUrl = `${MAGIC_FLOW_WEBHOOK_URL}/${generationId}/result`;
      const pollingResponse = await pollUntilCompleted({ url: pollUrl });

      const { resultFile, resultType, isVideo, isAudio } = buildFlowMedia({
        generationId,
        pollingResponse,
      });

      setResultFile(resultFile);
      const timestamp = Date.now();
      setGenerationTimestamp(timestamp);

      // Determine where the user is to decide notification vs auto-open
      const { screen: currentScreen, rightPanelTab } = getAppStore();

      const { filename } = await renameDriveFile({
        driveUid: resultFile.driveUid,
        presetTitle,
        resultType,
        timestamp,
      });

      updateContentFile({
        uuid: placeholderUuid,
        updates: {
          uuid: resultFile.driveUid || placeholderUuid,
          url: resultFile.url,
          name: filename,
          type: resultFile.type,
          preview: {
            url: isVideo
              ? resultFile.poster
              : isAudio
              ? undefined
              : resultFile.url,
            width: resultFile.width,
            height: resultFile.height,
          },
          isLoading: false,
          generationStartTime: undefined,
          presetType: undefined,
        },
      });

      await clearGeneration(generationId);

      const decision = decidePostGenerationUX({
        screen: currentScreen,
        rightPanelTab,
        fileId: resultFile.driveUid || placeholderUuid,
      });

      if (decision.kind === 'openLightbox') {
        setPendingLightboxFileId(decision.fileId);
        setIsResultMode(true);
      } else if (decision.kind === 'showToast') {
        setLocalNotification(resultFile);
      }

      return {
        resultFile,
        isVideo,
        isAudio,
        driveUid: resultFile.driveUid,
        width: resultFile.width,
        height: resultFile.height,
      };
    },
    [
      setResultFile,
      setGenerationTimestamp,
      setLocalNotification,
      updateContentFile,
      setPendingLightboxFileId,
      setIsResultMode,
    ]
  );

  return { poll };
};

export default useGenerationPolling;
